import { useState } from 'react';
import SideSheet from '..';
import { formatToNaira, copyToClipboard } from '../../../helpers/utils';
import { LuUploadCloud } from 'react-icons/lu';
import { IoCopyOutline } from 'react-icons/io5';
import useGetIt from '../../../hooks/useGetIt';
import './GetItRefundDetails.scss';
import ModalExecuteAction from '../../Modals/ModalExecuteAction';
import { Alert } from '@piggytechng/component-library';
import useCustomers from '../../../hooks/useCustomers';

import { GetItRefundDetailsProps } from '../../../types';

const GetItRefundDetails = ({ show, onClose, data }: GetItRefundDetailsProps) => {
  const { singleCustomer } = useCustomers();
  const { deliveryStatus, refundUndeliveredSubscription, isPerformingAction } = useGetIt({
    subId: data?.reference,
    enabled: !!data?.reference && show,
  });
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationProps, setConfirmationProps] = useState({
    text: '',
    action: () => {},
    actionColor: '',
  });

  if (!data) return null;

  const handleApprove = () => {
    setConfirmationProps({
      text: 'approve this refund?',
      action: async () => {
        if (!data?.reference) return;
        await refundUndeliveredSubscription(data.reference, () => {
          setShowConfirmation(false);
          onClose();
        });
      },
      actionColor: '#F37932',
    });
    setShowConfirmation(true);
  };

  const handleReject = () => {
    setConfirmationProps({
      text: 'reject this refund?',
      action: () => {
        Alert.success('Refund rejected successfully');
        setShowConfirmation(false);
      },
      actionColor: '#F37932',
    });
    setShowConfirmation(true);
  };

  return (
    <SideSheet
      active={show}
      onClose={onClose}
      title="Refund Details"
      subTitle={
        <div className="flex-row items-center" style={{ gap: '8px', display: 'flex', alignItems: 'center' }}>
          <span>ID: {data.reference || 'pur_001'}</span>
          <IoCopyOutline
            className="pointer"
            size={16}
            onClick={() => copyToClipboard('Reference', data.reference || '')}
            title="Copy ID"
          />
        </div>
      }
    >
      <div className="getit-refund-sheet">
        <section className="status-section">
          <h4>Status</h4>
          <span className="status-badge refund">Refund</span>
        </section>

        <section className="info-grid">
          <div className="grid-item full">
            <h4>User Information</h4>
            <div className="flex-row">
              <div className="sub-item">
                <label>Name</label>
                <p>
                  {singleCustomer ? `${singleCustomer.firstName} ${singleCustomer.lastName}` : data.user_id || 'N/A'}
                </p>
              </div>
              <div className="sub-item">
                <label>Email</label>
                <p>{singleCustomer?.email || data.reference || 'N/A'}</p>
              </div>
            </div>
          </div>

          <div className="grid-item full">
            <h4>Product Information</h4>
            <div className="flex-row">
              <div className="sub-item">
                <label>Product</label>
                <p>{data.purchase.item.title || 'Product'}</p>
              </div>
              <div className="sub-item">
                <label>Total Amount</label>
                <p>{formatToNaira(data.amounts.target_amount)}</p>
              </div>
            </div>
            <div className="flex-row mt-4">
              <div className="sub-item">
                <label>Amount Paid</label>
                <p>{formatToNaira(data.amounts.total_saved)}</p>
              </div>
              <div className="sub-item">
                <label>Balance</label>
                <p>{formatToNaira(data.amounts.remaining_amount)}</p>
              </div>
            </div>
          </div>

          <div className="grid-item full">
            <h4>Payment Progress</h4>
            <div className="progress-container">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${data.amounts.progress || 0}%` }} />
              </div>
              <p className="progress-label">{data.amounts.progress || 0}% Complete</p>
            </div>
          </div>

          <div className="grid-item full">
            <h4>Delivery Address</h4>
            <div className="sub-item">
              <label>Address</label>
              <p>
                {deliveryStatus?.destination
                  ? `${deliveryStatus.destination.address}, ${deliveryStatus.destination.city}, ${deliveryStatus.destination.state}`
                  : 'N/A'}
              </p>
            </div>
          </div>

          <div className="grid-item full">
            <h4>Reason for Refund</h4>
            <div className="textarea-container">
              <textarea readOnly value={data.status || 'Refund requested'} />
            </div>
          </div>

          <div className="grid-item full">
            <h4>Upload Refund Evidence</h4>
            <div className="upload-zone">
              <div className="upload-content">
                <div className="upload-icon">
                  <LuUploadCloud size={24} />
                </div>
                <p className="upload-title">Upload Document</p>
                <p className="upload-subtitle">
                  <span className="highlight">Click to upload</span>
                </p>
                <p className="file-hint">PNG, JPG, PDF (max. 800x400px)</p>
              </div>
            </div>
          </div>
        </section>

        <div className="refund-actions">
          <button className="reject-btn" onClick={handleReject}>
            Reject Refund
          </button>
          <button className="approve-btn" onClick={handleApprove}>
            Approve Refund
          </button>
        </div>

        <ModalExecuteAction
          modalIsOpen={showConfirmation}
          setIsOpen={setShowConfirmation}
          isLoading={isPerformingAction}
          {...confirmationProps}
        />
      </div>
    </SideSheet>
  );
};

export default GetItRefundDetails;
